import { Outlet } from "react-router-dom";
import { useEffect, lazy, Suspense, useState } from "react";
import { useAuthStore } from "@/shared/stores/authStore";
import { useUIStore, resolveTheme } from "@/shared/stores/uiStore";
import { ToastContainer } from "@/shared/ui/components/shared/ToastContainer";
import { QueryControllerProvider } from "@/app/providers/QueryControllerProvider";
import { SidebarRealtimeProvider } from "@/features/sidebar-realtime/SidebarRealtimeProvider";
import { ContextBadgeSync } from "@/features/context-badges/useContextBadges";
import { ProjectsProvider } from "@/app/providers/ProjectsContext";
import { ShareEventsProvider } from "@/app/providers/ShareEventsProvider";
import AppHealthBanner from "@/shared/ui/sections/AppHealthBanner";
import { BuzzSidebarPanel } from "@/features/buzz/components/BuzzSidebarPanel";
import { BuzzNavButton } from "@/features/buzz/components/BuzzNavButton";
import { useUserPreferences } from "@/shared/data/buzz";
import { ContextSidebar } from "@/shared/ui/sections/sidebar/ContextSidebar";
import { useSidebarState } from "@/shared/ui/layouts/sidebar-layouts";
import { Button } from "@opal/components";
import { SvgSidebar, SvgSearchMenu, SvgEdit } from "@opal/icons";
import { cn } from "@/shared/lib/utils";
import styles from "./AppLayout.module.css";

// [ZKPA additions]
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { useMasterKey } from "@/shared/hooks/useMasterKey";
import { ZKPABootstrapModal } from "@/features/privacy/ui/ZKPABootstrapModal";
import { ZKPAUnlockModal } from "@/features/privacy/ui/ZKPAUnlockModal";
import { ZKPAUnsupportedBrowser } from "@/features/privacy/components/ZKPAUnsupportedBrowser";
import { ZKPAErrorBoundary } from "@/features/privacy/components/ZKPAErrorBoundary";
import { SeedPhraseSetupModal } from "@/features/privacy/ui/SeedPhraseSetupModal";
import { useTemplateCache } from "@/shared/lib/template-runtime/useTemplateCache";

/**
 * AppLayout — authenticated app shell.
 * Replaces Next.js /app/layout.tsx with React Router Outlet.
 *
 * Responsibilities:
 * - Auth initialization (hydrate authStore from token)
 * - Theme application (data-theme on <html>)
 * - Sidebar + main content layout
 * - Toast notifications
 */
export function AppLayout() {

  // [ZKPA] Browser support guard — phải check trước mọi thứ khác
  if (!MasterKeyManager.isSupported()) {
    return <ZKPAUnsupportedBrowser />;
  }

  const userId = useAuthStore(s => s.user?.id?.toString() ?? null);
  // [ZKPA] Anonymous users have no real password — skip ZKPA entirely
  const isAnonymousUser = useAuthStore(s => s.user?.is_anonymous_user ?? false);
  // [ZKPA] Get password from auth session — kept in memory only, cleared on logout
  const password = useAuthStore((s) => s.sessionPassword);

  // [ZKPA] Load Master Key vào memory (skip for anonymous)
  const zkpaState = useMasterKey({ userId: isAnonymousUser ? null : userId, password });

  // Preload user + system templates sau khi đăng nhập (TR-RULE-04)
  useTemplateCache();

  // [ZKPA] Setup modal state
  const [showBootstrapModal, setShowBootstrapModal] = useState(false);
  const [showSeedSetupModal, setShowSeedSetupModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  // Persist dismiss state in sessionStorage across navigations
  const [dismissedZKPA, setDismissedZKPA] = useState(() => {
    try {
      return typeof window !== "undefined" && sessionStorage.getItem("zkpa_dismissed") === "true";
    } catch {
      return false;
    }
  });

  const handleDismissZKPA = () => {
    setShowUnlockModal(false);
    setShowBootstrapModal(false);
    setDismissedZKPA(true);
    try {
      sessionStorage.setItem("zkpa_dismissed", "true");
    } catch (_) {}
  };

  // [ZKPA] Trigger correct UI based on ZKPA state
  // Anonymous users always bypass ZKPA — they have no login password
  useEffect(() => {
    if (isAnonymousUser) {
      // Anonymous users: no ZKPA — clear all modals
      setShowBootstrapModal(false);
      setShowSeedSetupModal(false);
      setShowUnlockModal(false);
      return;
    }
    if (zkpaState.status === "uninitialized") {
      // Never bootstrapped — show full setup wizard unless dismissed
      if (!dismissedZKPA) {
        setShowBootstrapModal(true);
      } else {
        setShowBootstrapModal(false);
      }
      setShowUnlockModal(false);
    } else if (zkpaState.status === "needs_unlock" || zkpaState.status === "error") {
      // Already bootstrapped, password lost after page refresh — prompt for password only
      if (!dismissedZKPA) {
        setShowUnlockModal(true);
      } else {
        setShowUnlockModal(false);
      }
      setShowBootstrapModal(false);
    } else {
      setShowBootstrapModal(false);
      setShowUnlockModal(false);
    }
  }, [zkpaState.status, isAnonymousUser, dismissedZKPA]);

  // Apply theme to <html data-theme>
  const theme = useUIStore((s) => s.theme);
  useEffect(() => {
    const resolved = resolveTheme(theme);
    document.documentElement.setAttribute("data-theme", resolved);
  }, [theme]);

  const sidebarOpen = useUIStore((s) => s.sidebarOpen);

  const [buzzUnreadCount, setBuzzUnreadCount] = useState(0);
  const [buzzPanelOpen, setBuzzPanelOpen] = useState(false);

  // TASK-BZ-OPT-01: Only show Buzz UI when user has opted in.
  const { data: preferences } = useUserPreferences();
  const buzzEnabled = preferences?.buzz_enabled ?? false;
  
  const { folded, setFolded } = useSidebarState();

  return (
    <ProjectsProvider>
      <QueryControllerProvider>
        <SidebarRealtimeProvider>
          <ContextBadgeSync />
          <ShareEventsProvider>
          <div className="flex flex-col h-screen">
            {/* Global health banner — flow layout, no fixed offset needed (BUG-LAYOUT-V1-001) */}
            <AppHealthBanner />

            <div className={styles.layout} data-sidebar-open={sidebarOpen}>
            {/* Context-aware Sidebar — renders correct sidebar per active context */}
            <ContextSidebar />

            {/* Main content area */}
            <main className={cn(styles.main, "relative")}>
              {/* Floating Action Bar (visible when sidebar is completely folded) */}
              {folded && (
                <div className="absolute top-3 left-3 z-40 flex flex-row items-center gap-1">
                  <Button 
                    icon={SvgSidebar} 
                    prominence="tertiary" 
                    size="sm" 
                    onClick={() => setFolded(false)}
                    tooltip="Open Sidebar"
                  />
                  <Button 
                    icon={SvgSearchMenu} 
                    prominence="tertiary" 
                    size="sm" 
                    tooltip="Search"
                  />
                  <Button 
                    icon={SvgEdit} 
                    prominence="tertiary" 
                    size="sm" 
                    tooltip="New Chat"
                  />
                </div>
              )}
              {/* Block main content rendering until ZKPA is unlocked/bypassed/dismissed */}
              {isAnonymousUser || zkpaState.status === "ready" || zkpaState.status === "browser_unsupported" || dismissedZKPA ? (
                <Outlet />
              ) : (
                <div className="flex items-center justify-center h-full w-full">
                  {zkpaState.status === "loading" && (
                    <div className="flex flex-col items-center gap-4 text-gray-400">
                      <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full" />
                      <p>Đang tải khoá bảo mật...</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Floating Buzz Nav Button — only shown when user has enabled Buzz (TASK-BZ-OPT-01) */}
              {buzzEnabled && (
                <div className="fixed bottom-4 right-4 z-40">
                  <BuzzNavButton
                    active={buzzPanelOpen}
                    unreadCount={buzzUnreadCount}
                    onClick={() => setBuzzPanelOpen(prev => !prev)}
                  />
                </div>
              )}
            </main>

            {/* Global toast notifications */}
            <ToastContainer />
            
            {/* [ZKPA] Bootstrap modal — shown when ZKPA not configured for the first time */}
            <ZKPABootstrapModal
              open={showBootstrapModal && !showSeedSetupModal}
              onStartSetup={() => {
                setShowBootstrapModal(false);
                setShowSeedSetupModal(true);
              }}
              onDismiss={handleDismissZKPA}
            />

            {/* [ZKPA] Unlock modal — shown after page refresh when password is not in memory */}
            <ZKPAUnlockModal
              open={showUnlockModal}
              userId={userId ?? ""}
              onUnlocked={() => {
                setShowUnlockModal(false);
                setDismissedZKPA(false);
                try {
                  sessionStorage.removeItem("zkpa_dismissed");
                } catch (_) {}
              }}
              onDismiss={handleDismissZKPA}
            />
            
            {/* [ZKPA] Seed phrase setup — shown after user clicks "Bắt đầu thiết lập" */}
            {showSeedSetupModal && (
              <ZKPAErrorBoundary>
                <SeedPhraseSetupModal
                  open={showSeedSetupModal}
                  userId={userId ?? ""}
                  password={password ?? ""}
                  onComplete={() => setShowSeedSetupModal(false)}
                />
              </ZKPAErrorBoundary>
            )}
            
            {/* BuzzSidebarPanel — only mounted when Buzz is enabled (TASK-BZ-OPT-01).
                Also passes buzzEnabled to useBuzzSession so session-token is only
                fetched after the user has opted in. */}
            {buzzEnabled && (
              <BuzzSidebarPanel
                defaultOpen={buzzPanelOpen}
                onUnreadCountChange={setBuzzUnreadCount}
              />
            )}
            </div>{/* closes styles.layout */}
          </div>{/* closes flex flex-col h-screen */}
        </ShareEventsProvider>
        </SidebarRealtimeProvider>
      </QueryControllerProvider>
    </ProjectsProvider>
  );
}
